import { createFeatureSelector, createSelector } from '@ngrx/store';
import { defaults, filter, find, includes, last, reject } from 'lodash';

import { QuoteRouterSelectors } from '@lockton-apps/shared/data-access/router';
import {
  isQuoteStatusAfterOrEqual,
  Partners,
  QUOTE_PERMISSIONS,
  QUOTE_STATUSES,
  QUOTE_TYPES,
  QuoteAPIPermissions,
} from '@lockton-apps/shared/interfaces';

import {
  QUOTES_FEATURE_KEY,
  quotesAdapter,
  QuotesState,
} from './quotes.reducer';
import { getDevConfig, hasPermission } from '@lockton-apps/shared/util/helpers';

// Lookup the 'LsosQuotes' feature state managed by NgRx
export const selectQuotesState =
  createFeatureSelector<QuotesState>(QUOTES_FEATURE_KEY);

const { selectAll, selectEntities } = quotesAdapter.getSelectors();

export const selectQuotesLoaded = createSelector(
  selectQuotesState,
  (state: QuotesState) => state.loaded
);

export const selectQuotesError = createSelector(
  selectQuotesState,
  (state: QuotesState) => state.error
);

export const selectAllQuotes = createSelector(
  selectQuotesState,
  (state: QuotesState) =>
    reject(
      selectAll(state),
      (q) =>
        q.status === QUOTE_STATUSES.Defunct ||
        hasPermission(q, QUOTE_PERMISSIONS.isHidden)
    )
);

export const selectQuotesEntities = createSelector(
  selectQuotesState,
  (state: QuotesState) => selectEntities(state)
);

export const selectSelectedId = createSelector(
  selectQuotesState,
  (state: QuotesState) => state.selectedId
);

export const selectLoaded = createSelector(
  selectQuotesState,
  (state: QuotesState) => state.loaded
);

export const selectUpdating = createSelector(
  selectQuotesState,
  (state: QuotesState) => state.updating
);

export const selectEntity = createSelector(
  selectQuotesEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const selectRoutedQuote = createSelector(
  selectQuotesEntities,
  QuoteRouterSelectors.selectRoutedId,
  (entities, routedId) => (routedId ? entities[routedId] : undefined)
);

export const selectEntityAndLoaded = createSelector(
  selectEntity,
  selectLoaded,
  (selectedQuote, quotesLoaded) => ({ selectedQuote, quotesLoaded })
);

export const selectLiveQuotes = createSelector(selectAllQuotes, (entities) =>
  reject(entities, (q) =>
    includes([QUOTE_STATUSES.Complete, QUOTE_STATUSES.Defunct], q.status)
  )
);

export const selectCompletedQuotes = createSelector(
  selectAllQuotes,
  (entities) => filter(entities, (q) => q.status === QUOTE_STATUSES.Complete)
);

export const selectNewBusinessQuotes = createSelector(
  selectAllQuotes,
  (entities) =>
    filter(entities, (quote) => quote.type === QUOTE_TYPES.NewBusiness)
);

export const selectMTAs = createSelector(selectAllQuotes, (entities) =>
  filter(entities, (quote) => quote.type === QUOTE_TYPES.MTA)
);

export const selectRenewals = createSelector(selectAllQuotes, (entities) =>
  filter(entities, (quote) => quote.type === QUOTE_TYPES.Renewal)
);

export const selectLatestPurchasedQuote = createSelector(
  selectAllQuotes,
  (entities) =>
    last(filter(entities, (q) => q.status === QUOTE_STATUSES.Complete))
);

export const selectLatestQuote = createSelector(selectAllQuotes, (entities) =>
  last(entities)
);

export const selectQuoteStatus = createSelector(
  selectEntity,
  (entity) => entity?.status
);

export const selectRoutedQuoteType = createSelector(
  selectRoutedQuote,
  (entity) => entity?.type
);

export const selectRoutedMainProduct = createSelector(
  selectRoutedQuote,
  (quote) =>
    find(quote?.addons, { classOfBusinessId: quote?.classOfBusinessId })
);

export const selectAllRoutedQuoteAddons = createSelector(
  selectRoutedQuote,
  (quote) =>
    reject(quote?.addons, { classOfBusinessId: quote?.classOfBusinessId })
);

export const selectEnabledRoutedQuoteAddons = createSelector(
  selectAllRoutedQuoteAddons,
  (addons) => filter(addons, { enabled: true })
);

export const selectQuotePermissions = createSelector(selectEntity, (quote) => {
  if (!quote || getDevConfig('proposalForm.runLocally')) {
    return buildMockedPermissions();
  }

  if (
    quote.portalId === Partners.LSOS ||
    quote.portalId === Partners.NORWAY_PI
  ) {
    return buildPermissions({
      canSubmitProposal: !hasPermission(
        quote,
        QUOTE_PERMISSIONS.isProposalReadonly
      ),
      canUpdateProposal: !hasPermission(
        quote,
        QUOTE_PERMISSIONS.isProposalReadonly
      ),
      canReturnProposal: !hasPermission(
        quote,
        QUOTE_PERMISSIONS.isProposalReadonly
      ),
      canPayByPremiumFinance: hasPermission(
        quote,
        QUOTE_PERMISSIONS.canPayByPremiumFinance
      ),
    });
  }

  switch (quote.portalId) {
    case Partners.ACCA:
      return buildPermissions({
        canSendRenewalInvite: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canSendRenewalInvite
        ),
        canUpdateAdjustment: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canUpdateAdjustment
        ),
        canUpdateAddons: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canUpdateAddons
        ),
        canUpdateOptions: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canUpdateOptions
        ),
        canSendQuote: hasPermission(quote, QUOTE_PERMISSIONS.canSendQuote),
        canAcceptQuote: hasPermission(quote, QUOTE_PERMISSIONS.canAcceptQuote),
        canPay: hasPermission(quote, QUOTE_PERMISSIONS.canPay),
        canPayByPremiumFinance: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canPayByPremiumFinance
        ),
        canReturnProposal: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canReturnProposal
        ),
        canSubmitProposal: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canSubmitProposal
        ),
        canUpdateProposal: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canUpdateProposal
        ),
        canUpdateEndorsements: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canUpdateEndorsements
        ),
      });

    case Partners.ENW:
    case Partners.ACCA_OVER:
    case Partners.CONSTRUCTION:
    case Partners.REGULATORY:
      return buildPermissions({
        canSubmitProposal: hasPermission(
          quote,
          QUOTE_PERMISSIONS.canSubmitProposal
        ),
        canUpdateProposal: !hasPermission(
          quote,
          QUOTE_PERMISSIONS.isProposalReadonly
        ),
        canReturnProposal: !hasPermission(
          quote,
          QUOTE_PERMISSIONS.isProposalReadonly
        ),
      });

    default:
      return buildPermissions({
        canReturnProposal: !isQuoteStatusAfterOrEqual(
          quote.status,
          QUOTE_STATUSES.Quoted
        ),
        canSubmitProposal: !isQuoteStatusAfterOrEqual(
          quote.status,
          QUOTE_STATUSES.Quoted
        ),
        canUpdateProposal: !isQuoteStatusAfterOrEqual(
          quote.status,
          QUOTE_STATUSES.Quoted
        ),
      });
  }
});

function buildPermissions(
  permissions: Partial<QuoteAPIPermissions>
): QuoteAPIPermissions {
  return defaults(permissions, {
    canReturnProposal: false,
    canSubmitProposal: false,
    canUpdateProposal: false,
    canSendRenewalInvite: false,
    canUpdateAdjustment: false,
    canUpdateAddons: false,
    canUpdateOptions: false,
    canSendQuote: false,
    canAcceptQuote: false,
    canPay: false,
    canPayByPremiumFinance: false,
    canUpdateEndorsements: false,
    isHidden: false,
    isProposalReadonly: false,
    isQuoteReadonly: false,
  });
}

function buildMockedPermissions() {
  return {
    canReturnProposal: hasMockedPermission(QUOTE_PERMISSIONS.canReturnProposal),
    canSubmitProposal: hasMockedPermission(QUOTE_PERMISSIONS.canSubmitProposal),
    canUpdateProposal: hasMockedPermission(QUOTE_PERMISSIONS.canUpdateProposal),
    canSendRenewalInvite: hasMockedPermission(
      QUOTE_PERMISSIONS.canSendRenewalInvite
    ),
    canUpdateAdjustment: hasMockedPermission(
      QUOTE_PERMISSIONS.canUpdateAdjustment
    ),
    canUpdateAddons: hasMockedPermission(QUOTE_PERMISSIONS.canUpdateAddons),
    canUpdateOptions: hasMockedPermission(QUOTE_PERMISSIONS.canUpdateOptions),
    canSendQuote: hasMockedPermission(QUOTE_PERMISSIONS.canSendQuote),
    canAcceptQuote: hasMockedPermission(QUOTE_PERMISSIONS.canAcceptQuote),
    canPay: hasMockedPermission(QUOTE_PERMISSIONS.canPay),
    canPayByPremiumFinance: hasMockedPermission(
      QUOTE_PERMISSIONS.canPayByPremiumFinance
    ),
    canUpdateEndorsements: hasMockedPermission(
      QUOTE_PERMISSIONS.canUpdateEndorsements
    ),
    isHidden: hasMockedPermission(QUOTE_PERMISSIONS.isHidden),
    isProposalReadonly: hasMockedPermission(
      QUOTE_PERMISSIONS.isProposalReadonly
    ),
    isQuoteReadonly: hasMockedPermission(QUOTE_PERMISSIONS.isQuoteReadonly),
  };
}

function hasMockedPermission(permission: string): boolean {
  return includes(getDevConfig('quote.permissions'), permission);
}
